/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the header component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { NavLink, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faGlobeAmericas } from '@fortawesome/pro-duotone-svg-icons/faGlobeAmericas';


/**
 * Used to create a style hook to utilise JSS
 */
import { Theme, makeStyles } from '@material-ui/core/styles';

/**
 * Used to signify the header as the appbar of the application.
 */

/**
 * Used to restrict the content width of the header.
 */

/**
 * Used to layout the header.
 */

/**
 * Used to layout the header.
 */

/**
 * Used to format text.
 */

/**
 * Used to style navigation.
 */

/**
 * Used to style links in the navigation.
 */
import Button from '@material-ui/core/Button';



/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to get the BCT Website URL.
 */

/*
 * ---------------------------------------------------------------------------------
 * Assets
 * ---------------------------------------------------------------------------------
 */



import { faBuilding } from '@fortawesome/pro-duotone-svg-icons/faBuilding';
import { faFileAlt } from '@fortawesome/pro-duotone-svg-icons/faFileAlt';
import { faUserCircle } from '@fortawesome/pro-duotone-svg-icons/faUserCircle';
import { ALL_COUNTRIES, ALL_COUNTRIES_CODE, ALL_INSTITUTIONS, ALL_INSTITUTIONS_CODE, EventTypeContext, FormTypeContext, InstitutionContext, onlinePatientManagement, PatientContext as OpmsPatientContext } from '@ngt/online-patient-management-ui';
import BreadcrumbContainer from './BreadcrumbContainer';
import { Patient } from '../../api/Neon.Dtos';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IPatientBreadcrumbsProps {
    countryName?: string;
    institutionName?: string;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    navLink: {
        borderRadius: 50,
        fontSize: '0.75rem',
        fontWeight: 500,
        textTransform: 'none',
        margin: theme.spacing(0.25, 0),

        // '&:hover': {
        //     backgroundColor: onlinePatientManagement.colors.secondary[50]
        // }
    },
}));

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const PatientBreadcrumbs: React.FunctionComponent<IPatientBreadcrumbsProps> = ({
    countryName,
    institutionName
}) => {
    const classes = useStyles();

    const params = useParams();

    const [eventTypeState] = React.useContext(EventTypeContext);

    const [formTypeState] = React.useContext(FormTypeContext);

    const countryCode = params[onlinePatientManagement.routeParameters.countryCode]
    const institutionCode = params[onlinePatientManagement.routeParameters.institutionCode]
    const patientStudyNumber = params[onlinePatientManagement.routeParameters.patientStudyNumber]
    const eventTypeCode = params[onlinePatientManagement.routeParameters.eventTypeCode] ?? eventTypeState?.code
    const eventRepeat = params[onlinePatientManagement.routeParameters.eventRepeat]
    const formTypeCode = params[onlinePatientManagement.routeParameters.formTypeCode] ?? formTypeState?.code
    const formRepeat = params[onlinePatientManagement.routeParameters.formRepeat]

    const [institutionState] = React.useContext(InstitutionContext);

    const country = countryCode ? countryCode !== ALL_COUNTRIES_CODE ? institutionState?.country ?? countryName : ALL_COUNTRIES : ALL_COUNTRIES;
    const countryLink = countryCode && countryCode !== ALL_COUNTRIES_CODE ? `/registration/${countryCode}` : '/registration';

    const institution = institutionCode ? institutionCode !== ALL_INSTITUTIONS_CODE ? institutionState?.name ?? institutionName : ALL_INSTITUTIONS : undefined;

    const institutionCompleteLink = `/registration/${countryCode}/${institutionCode}`

    const institutionLink = institutionCode && institutionCode !== ALL_INSTITUTIONS_CODE ?
        countryCode && countryCode !== ALL_COUNTRIES_CODE ?
            `/registration/${countryCode}/${institutionCode}` :
            `/registration/${ALL_COUNTRIES_CODE}/${institutionCode}` :
        countryLink

    const patientLink = patientStudyNumber ? `${institutionCompleteLink}/${patientStudyNumber}` : institutionCompleteLink

    const eventTypeLink = `${patientLink}/${eventTypeCode}/${eventRepeat}`

    const formTypeLink = patientStudyNumber ? `${eventTypeLink}/${formTypeCode}/${formRepeat}` : `${institutionCompleteLink}/add-patient`

    

    return (
        <BreadcrumbContainer>
            <Button
                component={NavLink}
                to={countryLink}
                color="inherit"
                className={classes.navLink}
                activeClassName={classes.navLinkActive}
                startIcon={<FontAwesomeIcon icon={faGlobeAmericas} fixedWidth />}
                size="small"
            >
                {country}
            </Button>
            {
                !!institution && (
                    <Button
                        component={NavLink}
                        to={institutionLink}
                        color="inherit"
                        className={classes.navLink}
                        activeClassName={classes.navLinkActive}
                        startIcon={<FontAwesomeIcon icon={faBuilding} fixedWidth />}
                        size="small"
                    >
                        {institution}
                    </Button>
                )
            }
            {
                !!patientStudyNumber && (
                    <Button
                        component={NavLink}
                        to={patientLink}
                        color="inherit"
                        className={classes.navLink}
                        activeClassName={classes.navLinkActive}
                        startIcon={<FontAwesomeIcon icon={faUserCircle} fixedWidth />}
                        size="small"
                    >
                        {patientStudyNumber}
                    </Button>
                )
            }
            {
                !!formTypeState && !patientStudyNumber && (
                    <Button
                        component={NavLink}
                        to={formTypeLink}
                        color="inherit"
                        className={classes.navLink}
                        activeClassName={classes.navLinkActive}
                        startIcon={<FontAwesomeIcon icon={patientStudyNumber ? faFileAlt : faUserCircle} fixedWidth />}
                        size="small"
                    >
                        Add Patient
                    </Button>
                )
            }
            {
                !!formTypeState && !!patientStudyNumber && (
                    <FormButton
                        eventTypeLink={eventTypeLink}
                        institutionCompleteLink={institutionCompleteLink}
                        formTypeLink={formTypeLink}
                    />
                )
            }
        </BreadcrumbContainer>
    );
};

const FormButton: React.FunctionComponent<{ eventTypeLink: string; institutionCompleteLink: string; formTypeLink: string }> = ({
    eventTypeLink,
    institutionCompleteLink,
    formTypeLink
}) => {


    const classes = useStyles();

    const params = useParams();

    const [formTypeState] = React.useContext(FormTypeContext);

    const patientStudyNumber = params[onlinePatientManagement.routeParameters.patientStudyNumber]

    const formTypeCode = params[onlinePatientManagement.routeParameters.formTypeCode] ?? formTypeState?.code

    const formRepeat = params[onlinePatientManagement.routeParameters.formRepeat]

    const [patient] = React.useContext(OpmsPatientContext);

    const patientCohortC = (patient as Patient)?.treatmentId !== 1 && (patient as Patient)?.treatmentId !== 2;

    const formName = formTypeState?.code === 'pre-registration' && patientCohortC ? 'Screening Form' : formTypeState?.name;

    const formType = formTypeCode ? `${formName}${(formRepeat && formRepeat !== '1' ? ` (${formRepeat})` : '')}` : undefined

    
    return <>
        <Button
            component={NavLink}
            to={formTypeLink}
            color="inherit"
            className={classes.navLink}
            activeClassName={classes.navLinkActive}
            startIcon={<FontAwesomeIcon icon={patientStudyNumber ? faFileAlt : faUserCircle} fixedWidth />}
            size="small"
        >
            {
                formType 
            }
        </Button>
    </>
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default PatientBreadcrumbs;